/**
 * Shipping hub — active SGS shipments ready for pickup.
 *
 * Ported from the Manufacturing Dashboard "Shipping Team" page (Appsmith).
 * Non-SGS org / Suntronic location branching has been removed; only the
 * Smart Grid Solutions (location_id = 1) code paths are kept.
 *
 * Implemented:
 *   - Active shipment list (auto-flipped sent_to_pack_out, not yet shipped)
 *   - Toggle "Ready for Pickup"
 *   - Mark Picked Up / Mark Shipped modal (carrier + tracking)
 *   - Manual carrier/tracking save
 *   - Packing slip PDF generation and direct office print (boxes-pdf wizard with optional pallet mode; pallet layout persisted on the shipment for reprints)
 *   - International customer freight docs: commercial invoice + packing slips with carton dims/weights (no UPS)
 *   - UPS label creation, reprint, void, and ZPL print (domestic shipments and transfers)
 *   - Reprint Pre-Pack carton labels (select one or more sealed cartons)
 */

import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Download, Loader2, Printer, RefreshCw, Truck, XCircle } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { Input, Label } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";
import { formatSalesOrderQuantity, parseSalesOrderQuantity } from "@/lib/salesOrderQuantity";
import { recordHubSupportConsoleLine } from "@/lib/installHubSupportConsoleCapture";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { SHIPPING_LABOR_PAGE } from "@/lib/labor/laborPageKey";
import { playPairFailureSound } from "@/lib/floorStationPairAudio";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { useLaborSession } from "@/lib/labor/useLaborSession";

import { PREPACK_LABOR_FLASH_STORAGE_KEY } from "@/pages/prepack/prepackLaborFlash";
import type {
  LabelImage,
  OutboundMarkShippedTarget,
  OutboundPackingSlipTarget,
  OutboundQueueEntry,
  OutboundUpsTarget,
  PackingSlipRow,
  PackingValue,
  ShipmentRow,
  ShippingCarrierRow,
  ShippingUpsView,
  UpsPackageInput,
} from "./shippingHubTypes";
import { shipmentPackageDetailsAreComplete } from "./shippingHubTypes";
import {
  UPS_STANDARD_CARTON,
  buildUpsPackageDrafts,
  cartonUnitCountsFromSkuQuantities,
} from "./upsPackageDefaults";
import { OutboundQueueCard } from "./outboundQueueCard";
import { isInternationalShipTo } from "./internationalShipTo";
import {
  OutboundReprintCartonLabelModal,
  outboundReprintTargetFromEntry,
  type OutboundReprintCartonLabelTarget,
} from "./OutboundReprintCartonLabelModal";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import {
  cupsPrintersForDocumentPrint,
  fetchMiscCupsPrinters,
  formatCupsPrinterOptionLabel,
  MISC_CUPS_PRINTERS_QUERY_KEY,
  pdfBase64ToPrintFile,
  postMiscCupsPrint,
  SHIPPING_OUTBOUND_CUPS_PRINTER_STORAGE_KEY,
} from "@/lib/miscPrintingApi";
import {
  createTransferUpsShipment,
  createUpsShipment,
  fetchCarriers,
  fetchPackingSlipRows,
  fetchShipments,
  fetchShipmentPackageDetails,
  fetchTransferPackingSlipRows,
  fetchTransferUpsLabelData,
  fetchTransfers,
  fetchUpsLabelData,
  fetchUpsLabels,
  generateCommercialInvoicePdf,
  generatePackingSlipPdf,
  generateTransferPackingSlipPdf,
  markShipped,
  markTransferSent,
  printZplLabel,
  saveShipmentPackageDetails,
  shipmentPackageDetailsQueryKey,
  CARRIERS_QUERY_KEY,
  SHIPMENTS_QUERY_KEY,
  toggleReady,
  toggleTransferReady,
  TRANSFERS_QUERY_KEY,
  voidTransferUpsShipment,
  voidUpsShipment,
} from "./shippingHubApi";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function defaultCarrierName(carriers: ShippingCarrierRow[], existing: string | null | undefined): string {
  const trimmedExisting = existing?.trim();
  if (trimmedExisting) {
    const exact = carriers.find((carrier) => carrier.name === trimmedExisting);
    if (exact) {
      return exact.name;
    }
  }
  const ups = carriers.find((carrier) => carrier.name === "UPS");
  if (ups) {
    return ups.name;
  }
  return carriers[0]?.name ?? trimmedExisting ?? "";
}

function formatShipByDate(raw: string | null): string {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return raw;
  }
}

function shipBySortKey(entry: OutboundQueueEntry): number {
  if (entry.kind === "shipment") {
    return entry.shipment.mfr_ship_by_date
      ? new Date(entry.shipment.mfr_ship_by_date).getTime()
      : Number.POSITIVE_INFINITY;
  }
  return entry.transfer.ship_by_date
    ? new Date(entry.transfer.ship_by_date).getTime()
    : Number.POSITIVE_INFINITY;
}

function downloadPdf(base64: string, filename: string) {
  const link = document.createElement("a");
  link.href = `data:application/pdf;base64,${base64}`;
  link.download = filename;
  link.click();
}

function downloadPng(base64: string, filename: string) {
  const link = document.createElement("a");
  link.href = `data:image/png;base64,${base64}`;
  link.download = filename;
  link.click();
}

const DEFAULT_PACKAGE_LENGTH = "22";
const DEFAULT_PACKAGE_WIDTH = "22";
const DEFAULT_PACKAGE_HEIGHT = "12";
const DEFAULT_PACKAGE_WEIGHT = "1";

type PackageDimDraft = {
  length: string;
  width: string;
  height: string;
  weight: string;
};

function emptyPackageDimDraft(): PackageDimDraft {
  return {
    length: DEFAULT_PACKAGE_LENGTH,
    width: DEFAULT_PACKAGE_WIDTH,
    height: DEFAULT_PACKAGE_HEIGHT,
    weight: DEFAULT_PACKAGE_WEIGHT,
  };
}

function draftFromSavedPackage(saved: {
  length_in: number | null;
  width_in: number | null;
  height_in: number | null;
  weight_lbs: number | null;
} | undefined): PackageDimDraft {
  const draft = emptyPackageDimDraft();
  if (saved?.length_in != null) draft.length = String(saved.length_in);
  if (saved?.width_in != null) draft.width = String(saved.width_in);
  if (saved?.height_in != null) draft.height = String(saved.height_in);
  if (saved?.weight_lbs != null) draft.weight = String(saved.weight_lbs);
  return draft;
}

/** Map raw packing slip rows to boxes-pdf packingValues, inserting pallet assignments. */
function mapRowsToPackingValues(
  rows: PackingSlipRow[],
  palletByBox: Record<string, string>,
  usePalletMode: boolean,
): PackingValue[] {
  return rows
    .filter((row) => row.sales_order_number)
    .map((row) => ({
      salesOrderNumber: String(row.sales_order_number),
      sku: String(row.sku),
      box: Number(row.box),
      quantity: parseSalesOrderQuantity(row.quantity),
      pallet: usePalletMode ? String(palletByBox[String(row.box)] ?? "").trim() : "",
      batch_item_id: row.batch_item_id != null ? String(row.batch_item_id) : "",
      ...(row.sales_order_row_id != null && Number.isFinite(Number(row.sales_order_row_id))
        ? { salesOrderRowId: Number(row.sales_order_row_id) }
        : {}),
    }));
}

/** Group packing slip rows by box number for display in the modal table. */
function groupRowsByBox(rows: PackingSlipRow[]): Array<{
  box: number;
  skus: string;
  quantities: string;
  sales_order_numbers: string;
}> {
  const byBox: Record<number, PackingSlipRow[]> = {};
  for (const row of rows) {
    if (!byBox[row.box]) byBox[row.box] = [];
    byBox[row.box].push(row);
  }
  return Object.keys(byBox)
    .map(Number)
    .sort((a, b) => a - b)
    .map((box) => {
      const boxRows = byBox[box];
      return {
        box,
        skus: boxRows.map((r) => r.sku).join(", "),
        quantities: boxRows.map((r) => formatSalesOrderQuantity(r.quantity)).join(", "),
        sales_order_numbers: [...new Set(boxRows.map((r) => r.sales_order_number ?? ""))].join(", "),
      };
    });
}

// ---------------------------------------------------------------------------
// Mark Shipped modal
// ---------------------------------------------------------------------------

interface MarkShippedModalProps {
  target: OutboundMarkShippedTarget;
  carriers: ShippingCarrierRow[];
  carriersLoading: boolean;
  onClose: () => void;
  onConfirm: (carrier: string, tracking: string) => void;
  isPending: boolean;
}

function MarkShippedModal({
  target,
  carriers,
  carriersLoading,
  onClose,
  onConfirm,
  isPending,
}: MarkShippedModalProps) {
  const existingCarrier =
    target.kind === "shipment" ? target.row.carrier : target.row.carrier ?? target.row.latest_shipment?.carrier;
  const existingTracking =
    target.kind === "shipment" ? target.row.tracking : target.row.tracking ?? target.row.latest_shipment?.tracking;
  const [carrier, setCarrier] = useState(() => defaultCarrierName(carriers, existingCarrier));
  const [tracking, setTracking] = useState(existingTracking ?? "");

  useEffect(() => {
    setCarrier(defaultCarrierName(carriers, existingCarrier));
  }, [carriers, existingCarrier]);

  const title =
    target.kind === "shipment"
      ? `Mark Shipment ${String(target.row.shipment_id)} as Picked Up`
      : `Mark ${target.row.transfer_number} as Sent`;
  const subtitle =
    target.kind === "shipment" ? (
      <>
        SO: {target.row.manufacturer_sales_order_number ?? "—"} · Ship by:{" "}
        {formatShipByDate(target.row.mfr_ship_by_date)}
      </>
    ) : (
      <>
        To: {target.row.destination_name ?? "—"}
        {target.row.ship_by_date ? <> · Ship by {formatShipByDate(target.row.ship_by_date)}</> : null}
      </>
    );
  const confirmLabel = target.kind === "shipment" ? "Mark as Picked Up" : "Mark as Sent";
  const selectedCarrierRow = carriers.find((entry) => entry.name === carrier);
  const trackingRequired = selectedCarrierRow?.requires_tracking !== false;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-base font-semibold text-slate-900">{title}</h2>
        <p className="mb-4 text-sm text-slate-500">{subtitle}</p>
        <div className="space-y-4">
          <div>
            <Label htmlFor="ship-carrier">Carrier</Label>
            <select
              id="ship-carrier"
              value={carrier}
              onChange={(event) => setCarrier(event.target.value)}
              disabled={carriersLoading || carriers.length === 0}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50"
            >
              {carriers.length === 0 ? (
                <option value="">{carriersLoading ? "Loading carriers…" : "No carriers synced"}</option>
              ) : (
                carriers.map((entry) => (
                  <option key={entry.mv_shipping_provider_id} value={entry.name}>
                    {entry.name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <Label htmlFor="ship-tracking">Tracking number</Label>
            <Input
              id="ship-tracking"
              value={tracking}
              onChange={(event) => setTracking(event.target.value)}
              placeholder={trackingRequired ? "e.g. 1Z999AA10123456784" : "Optional for hand delivery"}
              className="mt-1"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isPending || carrier.trim() === "" || (trackingRequired && tracking.trim() === "")}
            onClick={() => onConfirm(carrier, tracking.trim())}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Packing slip modal
// ---------------------------------------------------------------------------

interface PackingSlipModalProps {
  target: OutboundPackingSlipTarget;
  freightMode?: boolean;
  onClose: () => void;
  onDone: () => void;
}

function PackingSlipModal({
  target,
  freightMode = false,
  onClose,
  onDone,
}: PackingSlipModalProps) {
  const queryClient = useQueryClient();
  const entityId = target.kind === "shipment" ? target.row.shipment_id : target.row.id;
  const queryKey =
    target.kind === "shipment"
      ? (["shipping", "packing-slip-rows", entityId, freightMode ? "freight" : "domestic"] as const)
      : (["shipping", "transfer-packing-slip-rows", entityId] as const);
  const [palletByBox, setPalletByBox] = useState<Record<string, string>>({});
  const [usePalletMode, setUsePalletMode] = useState(false);
  const [dimsByBox, setDimsByBox] = useState<Record<string, PackageDimDraft>>({});
  const [submitMode, setSubmitMode] = useState<"download" | "print" | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [printMessage, setPrintMessage] = useState<string | null>(null);
  const [selectedPrinter, setSelectedPrinter] = useState("");

  const printersQuery = useQuery({
    queryKey: MISC_CUPS_PRINTERS_QUERY_KEY,
    queryFn: fetchMiscCupsPrinters,
    staleTime: 60_000,
  });

  const printers = useMemo(
    () => cupsPrintersForDocumentPrint(printersQuery.data?.printers ?? []),
    [printersQuery.data?.printers],
  );

  useEffect(() => {
    if (selectedPrinter !== "" || printers.length === 0) return;
    const saved = localStorage.getItem(SHIPPING_OUTBOUND_CUPS_PRINTER_STORAGE_KEY);
    if (saved && printers.some((row) => row.name === saved)) {
      setSelectedPrinter(saved);
      return;
    }
    if (printers.length === 1) {
      setSelectedPrinter(printers[0]!.name);
    }
  }, [printers, selectedPrinter]);

  const submitting = submitMode !== null;

  const loadQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const bundle =
        target.kind === "shipment"
          ? await fetchPackingSlipRows(entityId)
          : await fetchTransferPackingSlipRows(entityId);
      if (bundle.rows.length === 0) {
        throw new Error(
          target.kind === "shipment"
            ? "No packed carton lines found for this shipment."
            : "No sealed transfer cartons found for this transfer.",
        );
      }
      const saved = bundle.pallet_allocation;
      const initial: Record<string, string> = {};
      const boxes = [...new Set(bundle.rows.map((row) => row.box))].sort((a, b) => a - b);
      for (const box of boxes) {
        const key = String(box);
        const fromSaved = saved?.pallet_by_box[key];
        initial[key] =
          typeof fromSaved === "string" && fromSaved.trim() !== "" ? fromSaved.trim() : "1";
      }
      setPalletByBox(initial);
      setUsePalletMode(freightMode ? false : Boolean(saved?.pallet_mode));
      if (freightMode && target.kind === "shipment") {
        const details = await fetchShipmentPackageDetails(entityId);
        const byBox = new Map(details.packages.map((pkg) => [pkg.box_number, pkg]));
        const initialDims: Record<string, PackageDimDraft> = {};
        for (const box of boxes) {
          initialDims[String(box)] = draftFromSavedPackage(byBox.get(box));
        }
        setDimsByBox(initialDims);
      } else {
        setDimsByBox({});
      }
      return bundle;
    },
    staleTime: 0,
  });

  if (loadQuery.isError) {
    const msg = loadQuery.error instanceof Error ? loadQuery.error.message : "Failed to load.";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <h2 className="mb-3 text-base font-semibold text-slate-900">
            {freightMode ? "Carton dims and weight" : "Create Packing Slip"}
          </h2>
          <p className="text-sm text-rose-700">{msg}</p>
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  const rows = loadQuery.data?.rows ?? null;
  const boxGroups = rows ? groupRowsByBox(rows) : [];
  const distinctBoxes = boxGroups.map((g) => g.box);

  function assignRoundRobin(palletCount: number) {
    const count = Math.max(1, palletCount);
    const updated: Record<string, string> = {};
    distinctBoxes.forEach((box, index) => {
      updated[String(box)] = String((index % count) + 1);
    });
    setPalletByBox(updated);
  }

  function updateDim(box: number, field: keyof PackageDimDraft, value: string) {
    setDimsByBox((prev) => ({
      ...prev,
      [String(box)]: { ...(prev[String(box)] ?? emptyPackageDimDraft()), [field]: value },
    }));
  }

  async function handleSavePackageDetails() {
    if (!rows || rows.length === 0 || target.kind !== "shipment") return;
    const parsedPackages: Array<{
      box_number: number;
      length_in: number;
      width_in: number;
      height_in: number;
      weight_lbs: number;
    }> = [];
    for (const box of distinctBoxes) {
      const draft = dimsByBox[String(box)] ?? emptyPackageDimDraft();
      const lengthIn = Number(draft.length);
      const widthIn = Number(draft.width);
      const heightIn = Number(draft.height);
      const weightLbs = Number(draft.weight);
      if (!Number.isFinite(lengthIn) || lengthIn <= 0) {
        setGenError(`Carton ${String(box)}: enter a length greater than zero.`);
        return;
      }
      if (!Number.isFinite(widthIn) || widthIn <= 0) {
        setGenError(`Carton ${String(box)}: enter a width greater than zero.`);
        return;
      }
      if (!Number.isFinite(heightIn) || heightIn <= 0) {
        setGenError(`Carton ${String(box)}: enter a height greater than zero.`);
        return;
      }
      if (!Number.isFinite(weightLbs) || weightLbs <= 0) {
        setGenError(`Carton ${String(box)}: enter a weight greater than zero.`);
        return;
      }
      parsedPackages.push({
        box_number: box,
        length_in: lengthIn,
        width_in: widthIn,
        height_in: heightIn,
        weight_lbs: weightLbs,
      });
    }

    setSubmitMode("download");
    setGenError(null);
    try {
      await saveShipmentPackageDetails(entityId, parsedPackages);
      void queryClient.invalidateQueries({ queryKey: shipmentPackageDetailsQueryKey(entityId) });
      onDone();
    } catch (error) {
      setGenError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setSubmitMode(null);
    }
  }

  async function handleConfirm(mode: "download" | "print") {
    if (freightMode) {
      await handleSavePackageDetails();
      return;
    }
    if (!rows || rows.length === 0) return;
    if (mode === "print" && !selectedPrinter.trim()) {
      setGenError("Select a printer first.");
      return;
    }
    if (usePalletMode) {
      const missing = distinctBoxes.some((box) => !String(palletByBox[String(box)] ?? "").trim());
      if (missing) {
        setGenError("Assign every carton to a pallet, or turn off pallet mode.");
        return;
      }
    }

    const packingValues = mapRowsToPackingValues(rows, palletByBox, usePalletMode);
    if (packingValues.length === 0) {
      setGenError(
        target.kind === "shipment"
          ? "No packing lines to submit (rows without a sales order number are excluded)."
          : "No packing lines to submit (rows without a transfer number are excluded).",
      );
      return;
    }

    setSubmitMode(mode);
    setGenError(null);
    setPrintMessage(null);
    try {
      const pdfBase64 =
        target.kind === "shipment"
          ? await generatePackingSlipPdf(entityId, usePalletMode, packingValues)
          : await generateTransferPackingSlipPdf(entityId, usePalletMode, packingValues);
      const filename =
        target.kind === "shipment"
          ? `Packing-slip-${String(entityId)}.pdf`
          : `Transfer-packing-slip-${target.row.transfer_number}.pdf`;

      if (mode === "download") {
        downloadPdf(pdfBase64, filename);
        void queryClient.invalidateQueries({ queryKey });
        onDone();
        return;
      }

      const printer = selectedPrinter.trim();
      const file = pdfBase64ToPrintFile(pdfBase64, filename);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("printer", printer);
      const result = await postMiscCupsPrint(formData);
      localStorage.setItem(SHIPPING_OUTBOUND_CUPS_PRINTER_STORAGE_KEY, result.printer);
      void queryClient.invalidateQueries({ queryKey });
      setPrintMessage(`Sent to ${result.printer}.`);
    } catch (error) {
      setGenError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setSubmitMode(null);
    }
  }

  const savedAllocation = loadQuery.data?.pallet_allocation;
  const showSavedPalletHint =
    Boolean(savedAllocation) &&
    (Boolean(savedAllocation?.pallet_mode) ||
      Object.keys(savedAllocation?.pallet_by_box ?? {}).length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {freightMode ? "Carton dims and weight" : "Create Packing Slip"}
            </h2>
            <p className="text-sm text-slate-500">
              {target.kind === "shipment" ? (
                <>
                  Shipment {String(entityId)} · {target.row.manufacturer_sales_order_number ?? "—"}
                </>
              ) : (
                <>
                  Transfer {target.row.transfer_number} · {target.row.destination_name ?? "—"}
                </>
              )}
            </p>
            {freightMode ? (
              <p className="mt-1 text-xs text-slate-500">
                Enter carton length, width, height (inches) and weight (pounds). These print on the packing slip
                and commercial invoice. Nothing is sent to UPS.
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loadQuery.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading carton lines…
            </div>
          ) : (
            <>
              {showSavedPalletHint && !freightMode ? (
                <p className="mb-3 text-xs text-slate-600">
                  This shipment has a saved pallet layout from a previous pallet packing slip. It is applied below;
                  change it if the physical pallets differ, then print or download again to update the saved layout.
                </p>
              ) : null}
              {freightMode ? (
                <div className="mb-4 space-y-3">
                  <p className="text-sm font-medium text-slate-700">Packages</p>
                  {boxGroups.map((group) => {
                    const draft = dimsByBox[String(group.box)] ?? emptyPackageDimDraft();
                    return (
                      <div key={group.box} className="rounded-md border border-slate-200 p-3">
                        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                          <span className="text-xs font-medium text-slate-600">Package {group.box}</span>
                          <span className="font-mono text-xs text-slate-500">
                            {group.skus} · qty {group.quantities}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {(["length", "width", "height"] as const).map((dim) => (
                            <div key={dim}>
                              <Label htmlFor={`freight-${String(group.box)}-${dim}`} className="text-xs capitalize">
                                {dim} (in)
                              </Label>
                              <Input
                                id={`freight-${String(group.box)}-${dim}`}
                                type="number"
                                min="0"
                                step="0.5"
                                value={draft[dim]}
                                onChange={(event) => updateDim(group.box, dim, event.target.value)}
                                className="mt-0.5 h-8 text-sm"
                              />
                            </div>
                          ))}
                          <div>
                            <Label htmlFor={`freight-${String(group.box)}-weight`} className="text-xs">
                              Weight (lbs) *
                            </Label>
                            <Input
                              id={`freight-${String(group.box)}-weight`}
                              type="number"
                              min="0"
                              step="0.1"
                              value={draft.weight}
                              onChange={(event) => updateDim(group.box, "weight", event.target.value)}
                              className="mt-0.5 h-8 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {/* Pallet mode toggle */}
              {!freightMode ? (
              <div className="mb-4 flex flex-wrap items-center gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={usePalletMode}
                    onChange={(event) => setUsePalletMode(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="font-medium text-slate-700">Pallet mode</span>
                </label>
                {usePalletMode ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Auto-assign:</span>
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => assignRoundRobin(n)}
                        className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50"
                      >
                        {n} pallet{n > 1 ? "s" : ""}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              ) : null}

              {/* Carton table */}
              {!freightMode ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-3">Box</th>
                      <th className="py-2 pr-3">SKU(s)</th>
                      <th className="py-2 pr-3 text-right">Qty</th>
                      <th className="py-2 pr-3">Sales Order</th>
                      {usePalletMode ? <th className="py-2">Pallet</th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {boxGroups.map((group) => (
                      <tr key={group.box} className="text-slate-700">
                        <td className="py-2 pr-3 font-medium">{group.box}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{group.skus}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{group.quantities}</td>
                        <td className="py-2 pr-3 text-xs text-slate-500">{group.sales_order_numbers || "—"}</td>
                        {usePalletMode ? (
                          <td className="py-2">
                            <Input
                              value={palletByBox[String(group.box)] ?? ""}
                              onChange={(event) =>
                                setPalletByBox((prev) => ({ ...prev, [String(group.box)]: event.target.value }))
                              }
                              className="h-7 w-16 text-center text-sm"
                              placeholder="1"
                            />
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              ) : null}

              {genError ? (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-rose-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  {genError}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4">
          {!freightMode ? (
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="ps-printer" className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
              Printer
            </Label>
            <select
              id="ps-printer"
              value={selectedPrinter}
              onChange={(event) => setSelectedPrinter(event.target.value)}
              disabled={submitting || printersQuery.isLoading}
              className="h-8 min-w-[12rem] flex-1 rounded-md border border-slate-300 bg-white px-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50"
            >
              {printers.length === 0 ? (
                <option value="">
                  {printersQuery.isLoading ? "Loading printers…" : "No letter printers found"}
                </option>
              ) : (
                printers.map((printer) => (
                  <option key={printer.name} value={printer.name}>
                    {formatCupsPrinterOptionLabel(printer)}
                  </option>
                ))
              )}
            </select>
          </div>
          ) : null}

          {printMessage ? <p className="text-sm text-emerald-700">{printMessage}</p> : null}

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            {freightMode ? (
              <Button
                variant="primary"
                onClick={() => void handleSavePackageDetails()}
                disabled={submitting || loadQuery.isLoading || boxGroups.length === 0}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  "Save carton dims and weight"
                )}
              </Button>
            ) : (
              <>
            <Button
              variant="secondary"
              onClick={() => void handleConfirm("print")}
              disabled={submitting || loadQuery.isLoading || boxGroups.length === 0 || !selectedPrinter.trim()}
            >
              {submitMode === "print" ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                  Printing…
                </>
              ) : (
                <>
                  <Printer className="mr-1.5 h-4 w-4" aria-hidden />
                  Print PDF
                </>
              )}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleConfirm("download")}
              disabled={submitting || loadQuery.isLoading || boxGroups.length === 0}
            >
              {submitMode === "download" ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                  Generating…
                </>
              ) : (
                <>
                  <Download className="mr-1.5 h-4 w-4" aria-hidden />
                  Download PDF
                </>
              )}
            </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FreightDocsPrintModalProps {
  shipment: ShipmentRow;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onPrint: (printer: string) => void;
}

function FreightDocsPrintModal({ shipment, pending, error, onClose, onPrint }: FreightDocsPrintModalProps) {
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const printersQuery = useQuery({
    queryKey: MISC_CUPS_PRINTERS_QUERY_KEY,
    queryFn: fetchMiscCupsPrinters,
    staleTime: 60_000,
  });
  const printers = useMemo(
    () => cupsPrintersForDocumentPrint(printersQuery.data?.printers ?? []),
    [printersQuery.data?.printers],
  );

  useEffect(() => {
    if (selectedPrinter !== "" || printers.length === 0) return;
    const saved = localStorage.getItem(SHIPPING_OUTBOUND_CUPS_PRINTER_STORAGE_KEY);
    if (saved && printers.some((row) => row.name === saved)) {
      setSelectedPrinter(saved);
      return;
    }
    if (printers.length === 1) {
      setSelectedPrinter(printers[0]!.name);
    }
  }, [printers, selectedPrinter]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-base font-semibold text-slate-900">Print packing slip and commercial invoice</h2>
        <p className="mt-1 text-sm text-slate-500">
          Shipment {String(shipment.shipment_id)} · {shipment.manufacturer_sales_order_number ?? "—"}
        </p>
        <div className="mt-4">
          <Label htmlFor="freight-docs-printer" className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Printer
          </Label>
          <select
            id="freight-docs-printer"
            value={selectedPrinter}
            onChange={(event) => setSelectedPrinter(event.target.value)}
            disabled={pending || printersQuery.isLoading}
            className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50"
          >
            {printers.length === 0 ? (
              <option value="">
                {printersQuery.isLoading ? "Loading printers…" : "No letter printers found"}
              </option>
            ) : (
              printers.map((printer) => (
                <option key={printer.name} value={printer.name}>
                  {formatCupsPrinterOptionLabel(printer)}
                </option>
              ))
            )}
          </select>
        </div>
        {error ? (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-rose-700">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={pending || selectedPrinter.trim() === ""}
            onClick={() => onPrint(selectedPrinter.trim())}
          >
            {pending ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                Printing…
              </>
            ) : (
              <>
                <Printer className="mr-1.5 h-4 w-4" aria-hidden />
                Print both PDFs
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UPS modal
// ---------------------------------------------------------------------------

interface UpsModalProps {
  target: OutboundUpsTarget;
  initialView: ShippingUpsView;
  initialLabels?: LabelImage[];
  onClose: () => void;
  onTrackingChanged: () => void;
  onShipmentActioned?: (entityId: number) => void;
}

function buildDefaultUpsPackages(
  cartonCount: number,
  cartonUnitCounts: number[],
): UpsPackageInput[] {
  return buildUpsPackageDrafts(cartonCount, cartonUnitCounts).map((draft, index) => ({
    id: index + 1,
    ...draft,
  }));
}

function UpsModal({ target, initialView, initialLabels, onClose, onTrackingChanged, onShipmentActioned }: UpsModalProps) {
  const entityId = target.kind === "shipment" ? target.row.shipment_id : target.row.id;
  const cartonCount =
    target.kind === "shipment" ? target.row.carton_count : target.row.box_count;
  const existingTracking = target.row.tracking ?? "";
  const [view, setView] = useState<ShippingUpsView>(initialView);
  const cartonUnitCounts =
    target.kind === "shipment" ? cartonUnitCountsFromSkuQuantities(target.row.sku_quantities) : [];
  const [packages, setPackages] = useState<UpsPackageInput[]>(() =>
    buildDefaultUpsPackages(cartonCount, cartonUnitCounts),
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [labels, setLabels] = useState<LabelImage[]>(initialLabels ?? []);
  const [tracking, setTracking] = useState<string>(existingTracking);
  const { station, setStation, selectedPrinter, unavailableMessage } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.shippingOutbound,
    labelSize: "4x6",
    dpi: 203,
    fetchPrintersEnabled: view === "result",
  });
  const [printing, setPrinting] = useState(false);
  const [printMessage, setPrintMessage] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  // Load address data for display
  const addressQuery = useQuery({
    queryKey:
      target.kind === "shipment"
        ? (["shipping", "ups-label-data", entityId] as const)
        : (["shipping", "transfer-ups-label-data", entityId] as const),
    queryFn: () =>
      target.kind === "shipment" ? fetchUpsLabelData(entityId) : fetchTransferUpsLabelData(entityId),
    staleTime: 60_000,
  });

  // Load printers when result view is active
  function addPackage() {
    setPackages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        length: UPS_STANDARD_CARTON.length,
        width: UPS_STANDARD_CARTON.width,
        height: UPS_STANDARD_CARTON.height,
        weight: UPS_STANDARD_CARTON.weight,
      },
    ]);
  }

  function removePackage(id: number) {
    setPackages((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePackage(id: number, field: keyof UpsPackageInput, value: string) {
    setPackages((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  }

  async function handleCreate() {
    setSubmitError(null);
    const parsed = packages.map((pkg, index) => ({
      packageNumber: index + 1,
      length: Number(pkg.length),
      width: Number(pkg.width),
      height: Number(pkg.height),
      weight: Number(pkg.weight),
    }));

    for (const pkg of parsed) {
      if (!Number.isFinite(pkg.length) || pkg.length <= 0) {
        const message = `Package ${pkg.packageNumber}: enter a length greater than zero.`;
        setSubmitError(message);
        recordHubSupportConsoleLine("error", {
          event: "ups_create_validation_failed",
          entity_kind: target.kind,
          entity_id: entityId,
          field: "length",
          package: pkg.packageNumber,
          packages,
        });
        return;
      }
      if (!Number.isFinite(pkg.width) || pkg.width <= 0) {
        const message = `Package ${pkg.packageNumber}: enter a width greater than zero.`;
        setSubmitError(message);
        recordHubSupportConsoleLine("error", {
          event: "ups_create_validation_failed",
          entity_kind: target.kind,
          entity_id: entityId,
          field: "width",
          package: pkg.packageNumber,
          packages,
        });
        return;
      }
      if (!Number.isFinite(pkg.height) || pkg.height <= 0) {
        const message = `Package ${pkg.packageNumber}: enter a height greater than zero.`;
        setSubmitError(message);
        recordHubSupportConsoleLine("error", {
          event: "ups_create_validation_failed",
          entity_kind: target.kind,
          entity_id: entityId,
          field: "height",
          package: pkg.packageNumber,
          packages,
        });
        return;
      }
      if (!Number.isFinite(pkg.weight) || pkg.weight <= 0) {
        const message = `Package ${pkg.packageNumber}: enter a weight greater than zero.`;
        setSubmitError(message);
        recordHubSupportConsoleLine("error", {
          event: "ups_create_validation_failed",
          entity_kind: target.kind,
          entity_id: entityId,
          field: "weight",
          package: pkg.packageNumber,
          packages,
        });
        return;
      }
    }

    const payload = parsed.map(({ length, width, height, weight }) => ({
      length,
      width,
      height,
      weight,
    }));

    setSubmitting(true);
    try {
      const result =
        target.kind === "shipment"
          ? await createUpsShipment(entityId, payload)
          : await createTransferUpsShipment(entityId, payload);
      setTracking(result.tracking);
      setLabels(result.labels);
      setView("result");
      onTrackingChanged();
      onShipmentActioned?.(entityId);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : String(error);
      setSubmitError(message);
      const responseBody =
        error instanceof ApiError && error.body != null && typeof error.body === "object"
          ? (error.body as { detail?: unknown })
          : null;
      const upsDetail = typeof responseBody?.detail === "string" ? responseBody.detail : null;
      recordHubSupportConsoleLine("error", {
        event: "ups_create_failed",
        entity_kind: target.kind,
        entity_id: entityId,
        packages: payload,
        operator_message: message,
        ups_detail: upsDetail,
        message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePrint() {
    if (!tracking || !selectedPrinter) return;
    const trackingNo = tracking.split("/")[0];
    if (!trackingNo) return;

    setPrinting(true);
    setPrintMessage(null);
    try {
      await printZplLabel(trackingNo, selectedPrinter);
      setPrintMessage("Labels sent to printer.");
    } catch (error) {
      setPrintMessage(error instanceof ApiError ? error.message : String(error));
    } finally {
      setPrinting(false);
    }
  }

  function handleDownload() {
    for (const label of labels) {
      const name = label.tracking_no ?? `label-${label.id}`;
      if (label.letter_pdf_base64) downloadPdf(label.letter_pdf_base64, `${name}-letter.pdf`);
      if (label.label_4x6_pdf_base64) downloadPdf(label.label_4x6_pdf_base64, `${name}-4x6.pdf`);
      if (!label.letter_pdf_base64 && !label.label_4x6_pdf_base64 && label.img_base64) {
        downloadPng(label.img_base64, `${name}.png`);
      }
    }
  }

  async function handleVoidConfirm() {
    const trackingNo = (existingTracking || tracking).split("/")[0];
    if (!trackingNo) return;

    setVoiding(true);
    setVoidError(null);
    try {
      if (target.kind === "shipment") {
        await voidUpsShipment(entityId, trackingNo);
      } else {
        await voidTransferUpsShipment(entityId, trackingNo);
      }
      onTrackingChanged();
      onClose();
    } catch (error) {
      setVoidError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setVoiding(false);
    }
  }

  const addr = addressQuery.data;
  const addressLines = addr
    ? [
        addr.ship_to_recipient,
        addr.ship_to_attention,
        addr.ship_to_address_line_1,
        addr.ship_to_address_line_2,
        addr.ship_to_address_line_3,
        [addr.ship_to_city, addr.ship_to_state, addr.ship_to_zip].filter(Boolean).join(" "),
        addr.ship_to_country !== "NONE" ? addr.ship_to_country : "",
      ]
        .filter(Boolean)
        .join("\n")
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {view === "create" ? "Create UPS Shipment" : view === "result" ? "UPS Shipment Labels" : "Void UPS Shipment"}
            </h2>
            <p className="text-sm text-slate-500">
              {target.kind === "shipment" ? (
                <>
                  Shipment {String(entityId)} · {target.row.manufacturer_sales_order_number ?? "—"}
                </>
              ) : (
                <>
                  Transfer {target.row.transfer_number} · {target.row.destination_name ?? "—"}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Ship-to address */}
          {(view === "create" || view === "void") && (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Ship To</p>
              {addressQuery.isLoading ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Loading…
                </div>
              ) : addressLines ? (
                <pre className="whitespace-pre-wrap font-sans text-slate-800">{addressLines}</pre>
              ) : (
                <p className="text-slate-400">Address not found</p>
              )}
              {addr?.po_number ? (
                <p className="mt-1 text-xs text-slate-500">PO: {addr.po_number}</p>
              ) : null}
            </div>
          )}

          {/* Create view */}
          {view === "create" && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Packages</p>
              {packages.map((pkg) => (
                <div key={pkg.id} className="rounded-md border border-slate-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-600">Package {pkg.id}</span>
                    {packages.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removePackage(pkg.id)}
                        className="text-xs text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {(["length", "width", "height"] as const).map((dim) => (
                      <div key={dim}>
                        <Label htmlFor={`pkg-${pkg.id}-${dim}`} className="text-xs capitalize">
                          {dim} (in)
                        </Label>
                        <Input
                          id={`pkg-${pkg.id}-${dim}`}
                          type="number"
                          min="0"
                          step="0.5"
                          value={pkg[dim]}
                          onChange={(event) => updatePackage(pkg.id, dim, event.target.value)}
                          className="mt-0.5 h-8 text-sm"
                        />
                      </div>
                    ))}
                    <div>
                      <Label htmlFor={`pkg-${pkg.id}-weight`} className="text-xs">
                        Weight (lbs) *
                      </Label>
                      <Input
                        id={`pkg-${pkg.id}-weight`}
                        type="number"
                        min="0"
                        step="0.1"
                        value={pkg.weight}
                        onChange={(event) => updatePackage(pkg.id, "weight", event.target.value)}
                        className="mt-0.5 h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addPackage}
                className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
              >
                + Add package
              </button>

              {submitError ? (
                <p className="flex items-center gap-1.5 text-sm text-rose-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  {submitError}
                </p>
              ) : null}
            </div>
          )}

          {/* Result view */}
          {view === "result" && (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-sm font-medium text-emerald-800">
                  UPS shipment created — tracking: {tracking || "—"}
                </p>
              </div>

              {/* Label images */}
              {labels.length > 0 ? (
                <div className="space-y-2">
                  {labels.map((label) =>
                    label.img_base64 ? (
                      <div key={label.id} className="flex flex-col items-center gap-1">
                        <p className="text-xs text-slate-500">
                          {label.tracking_no ?? `Label ${label.id}`}
                        </p>
                        <img
                          src={`data:image/png;base64,${label.img_base64}`}
                          alt={`Label ${label.id}`}
                          className="max-h-64 rounded border border-slate-200 object-contain"
                        />
                      </div>
                    ) : null,
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No label images available.</p>
              )}

              {/* Printer */}
              <div>
                <div className="flex flex-wrap items-end gap-2">
                  <ZplLocationSelect
                    id="ups-printer"
                    label="Location"
                    value={station}
                    onChange={setStation}
                    unavailableMessage={unavailableMessage}
                    className="min-w-[12rem] flex-1"
                  />
                  <Button size="sm" variant="secondary" onClick={() => void handlePrint()} disabled={printing || selectedPrinter.trim() === ""}>
                    {printing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Printer className="h-3.5 w-3.5" aria-hidden />}
                    Print
                  </Button>
                </div>
                {printMessage ? (
                  <p className="mt-1 text-xs text-slate-500">{printMessage}</p>
                ) : null}
              </div>

              {/* Void link */}
              <div className="pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setView("void")}
                  className="text-xs text-slate-400 hover:text-rose-600"
                >
                  <XCircle className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                  Void this UPS shipment
                </button>
              </div>
            </div>
          )}

          {/* Void view */}
          {view === "void" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                This will void tracking{" "}
                <span className="font-mono font-medium">{existingTracking || tracking}</span> with UPS and clear
                the carrier and tracking number from this {target.kind === "shipment" ? "shipment" : "transfer"}.
              </p>
              <p className="text-sm font-medium text-rose-700">This cannot be undone.</p>
              {voidError ? (
                <p className="flex items-center gap-1.5 text-sm text-rose-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  {voidError}
                </p>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          {view === "create" && (
            <>
              <Button variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleCreate()} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                    Creating…
                  </>
                ) : (
                  <>
                    <Truck className="mr-1.5 h-4 w-4" aria-hidden />
                    Create Shipment
                  </>
                )}
              </Button>
            </>
          )}

          {view === "result" && (
            <>
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              {labels.some((l) => l.letter_pdf_base64 || l.label_4x6_pdf_base64 || l.img_base64) ? (
                <Button variant="secondary" onClick={handleDownload}>
                  <Download className="mr-1.5 h-4 w-4" aria-hidden />
                  Download letter and 4 by 6
                </Button>
              ) : null}
            </>
          )}

          {view === "void" && (
            <>
              <Button variant="secondary" onClick={() => setView("result")} disabled={voiding}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleVoidConfirm()}
                disabled={voiding}
                className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
              >
                {voiding ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                    Voiding…
                  </>
                ) : (
                  "Confirm Void"
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Labor tracker
// ---------------------------------------------------------------------------

interface LaborTrackerHandle {
  /** Call after a shipment is shipped/picked up while a session is active. */
  recordShipment: (shipmentId: number) => void;
  /** Reset idle timer — call on any meaningful user action. */
  touchActivity: () => void;
}

const SharedLaborTracker = forwardRef<LaborTrackerHandle, Record<never, never>>(function SharedLaborTracker(_props, ref) {
  const navigate = useNavigate();
  const labor = useLaborSession({
    page: SHIPPING_LABOR_PAGE,
    domain: "shipping",
    moduleGate: "shipping",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Shipping",
    autoStart: false,
    onIdleTimeout: () => playPairFailureSound(),
    onSessionEndedRemotely: () => {
      playPairFailureSound();
      navigate("/shipping", { replace: true });
    },
    onStopped: () => {
      navigate("/shipping", { replace: true });
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      recordShipment(shipmentId: number) {
        void labor.recordScan({
          deviceSerial: `shipment:${shipmentId}`,
          target: { kind: "shipment", id: shipmentId },
        });
      },
      touchActivity() {
        labor.touchActivity();
      },
    }),
    [labor],
  );

  const shippingLaborDevPanelSnapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Shipping",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, shippingLaborDevPanelSnapshot);

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          variant={labor.isWorking ? "secondary" : "primary"}
          onClick={() => void (labor.isWorking ? labor.stopWorking() : labor.beginWorking())}
          disabled={labor.loading}
        >
          {labor.loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          {labor.isWorking ? "Stop Working" : "Start Working"}
        </Button>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ShippingHubPage() {
  const queryClient = useQueryClient();
  const laborRef = useRef<LaborTrackerHandle>(null);
  const [markShippedTarget, setMarkShippedTarget] = useState<OutboundMarkShippedTarget | null>(null);
  const [packingSlipOpen, setPackingSlipOpen] = useState<{
    target: OutboundPackingSlipTarget;
    freightMode: boolean;
  } | null>(null);
  const [freightPrintShipment, setFreightPrintShipment] = useState<ShipmentRow | null>(null);
  const [upsTarget, setUpsTarget] = useState<OutboundUpsTarget | null>(null);
  const [reprintCartonTarget, setReprintCartonTarget] = useState<OutboundReprintCartonLabelTarget | null>(null);
  const [upsView, setUpsView] = useState<ShippingUpsView>("create");
  const [upsInitialLabels, setUpsInitialLabels] = useState<LabelImage[] | undefined>(undefined);
  const [actionError, setActionError] = useState<string | null>(null);
  const [prePackLaborFlash, setPrePackLaborFlash] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PREPACK_LABOR_FLASH_STORAGE_KEY);
      if (raw != null && raw.trim() !== "") {
        sessionStorage.removeItem(PREPACK_LABOR_FLASH_STORAGE_KEY);
        setPrePackLaborFlash(raw);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const shipmentsQuery = useQuery({
    queryKey: SHIPMENTS_QUERY_KEY,
    queryFn: fetchShipments,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const transfersQuery = useQuery({
    queryKey: TRANSFERS_QUERY_KEY,
    queryFn: fetchTransfers,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const carriersQuery = useQuery({
    queryKey: CARRIERS_QUERY_KEY,
    queryFn: fetchCarriers,
    staleTime: 5 * 60_000,
  });

  const carriers = carriersQuery.data ?? [];

  const markTransferSentMutation = useMutation({
    mutationFn: ({ transferId, carrier, tracking }: { transferId: number; carrier: string; tracking: string }) =>
      markTransferSent(transferId, carrier, tracking),
    onSuccess: (_data, variables) => {
      laborRef.current?.recordShipment(variables.transferId);
      void queryClient.invalidateQueries({ queryKey: TRANSFERS_QUERY_KEY });
      setMarkShippedTarget(null);
      setActionError(null);
    },
    onError: (error) => {
      setActionError(`Could not mark transfer sent: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const toggleTransferReadyMutation = useMutation({
    mutationFn: (transferId: number) => toggleTransferReady(transferId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TRANSFERS_QUERY_KEY });
      setActionError(null);
    },
    onError: (error) => {
      setActionError(`Could not toggle transfer ready: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const toggleReadyMutation = useMutation({
    mutationFn: (shipmentId: number) => toggleReady(shipmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SHIPMENTS_QUERY_KEY });
      setActionError(null);
    },
    onError: (error) => {
      setActionError(`Could not toggle ready: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const markShippedMutation = useMutation({
    mutationFn: ({ shipmentId, carrier, tracking }: { shipmentId: number; carrier: string; tracking: string }) =>
      markShipped(shipmentId, carrier, tracking),
    onSuccess: (_data, variables) => {
      laborRef.current?.recordShipment(variables.shipmentId);
      void queryClient.invalidateQueries({ queryKey: SHIPMENTS_QUERY_KEY });
      setMarkShippedTarget(null);
      setActionError(null);
    },
    onError: (error) => {
      setActionError(`Could not mark shipped: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const markShippedWithTrackingMutation = useMutation({
    mutationFn: (
      variables:
        | { kind: "shipment"; entityId: number; carrier: string; tracking: string }
        | { kind: "transfer"; entityId: number; carrier: string; tracking: string },
    ) =>
      variables.kind === "shipment"
        ? markShipped(variables.entityId, variables.carrier, variables.tracking)
        : markTransferSent(variables.entityId, variables.carrier, variables.tracking),
    onSuccess: (_data, variables) => {
      laborRef.current?.recordShipment(variables.entityId);
      if (variables.kind === "shipment") {
        void queryClient.invalidateQueries({ queryKey: SHIPMENTS_QUERY_KEY });
      } else {
        void queryClient.invalidateQueries({ queryKey: TRANSFERS_QUERY_KEY });
      }
      setMarkShippedTarget(null);
      setActionError(null);
    },
    onError: (error) => {
      setActionError(`Could not mark shipped: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const freightDocsMutation = useMutation({
    mutationFn: async (variables: {
      shipmentId: number;
      mode: "download" | "print";
      printer?: string;
    }) => {
      const bundle = await fetchPackingSlipRows(variables.shipmentId);
      const packingValues = mapRowsToPackingValues(bundle.rows, {}, false);
      if (packingValues.length === 0) {
        throw new Error("No packing lines to submit (rows without a sales order number are excluded).");
      }
      const [slipBase64, invoice] = await Promise.all([
        generatePackingSlipPdf(variables.shipmentId, false, packingValues),
        generateCommercialInvoicePdf(variables.shipmentId),
      ]);
      if (variables.mode === "print") {
        const printer = String(variables.printer ?? "").trim();
        if (printer === "") {
          throw new Error("Select a printer first.");
        }
        const slipFile = pdfBase64ToPrintFile(slipBase64, `Packing-slip-${String(variables.shipmentId)}.pdf`);
        const invoiceFile = pdfBase64ToPrintFile(invoice.pdfBase64, invoice.filename);
        const slipForm = new FormData();
        slipForm.append("file", slipFile);
        slipForm.append("printer", printer);
        const slipResult = await postMiscCupsPrint(slipForm);
        const invoiceForm = new FormData();
        invoiceForm.append("file", invoiceFile);
        invoiceForm.append("printer", printer);
        await postMiscCupsPrint(invoiceForm);
        localStorage.setItem(SHIPPING_OUTBOUND_CUPS_PRINTER_STORAGE_KEY, slipResult.printer);
        return { mode: "print" as const, printer: slipResult.printer };
      }
      downloadPdf(slipBase64, `Packing-slip-${String(variables.shipmentId)}.pdf`);
      downloadPdf(invoice.pdfBase64, invoice.filename);
      return { mode: "download" as const };
    },
    onSuccess: (result) => {
      setActionError(null);
      if (result.mode === "print") {
        setFreightPrintShipment(null);
      }
    },
    onError: (error) => {
      setActionError(
        `Could not create packing slip and commercial invoice: ${error instanceof ApiError ? error.message : String(error)}`,
      );
    },
  });

  const shipments = shipmentsQuery.data ?? [];
  const transfers = transfersQuery.data ?? [];
  const loadError =
    shipmentsQuery.isError && shipmentsQuery.error instanceof Error ? shipmentsQuery.error.message : null;
  const transfersLoadError =
    transfersQuery.isError && transfersQuery.error instanceof Error ? transfersQuery.error.message : null;

  const internationalShipmentIds = useMemo(
    () =>
      shipments
        .filter((shipment) => isInternationalShipTo(shipment.ship_to_country, shipment.ship_to_state))
        .map((shipment) => shipment.shipment_id),
    [shipments],
  );
  const packageDetailsQueries = useQueries({
    queries: internationalShipmentIds.map((shipmentId) => ({
      queryKey: shipmentPackageDetailsQueryKey(shipmentId),
      queryFn: () => fetchShipmentPackageDetails(shipmentId),
      staleTime: 30_000,
    })),
  });
  const packageDetailsByShipmentId = useMemo(() => {
    const byId = new Map<number, { complete: boolean }>();
    for (let index = 0; index < internationalShipmentIds.length; index += 1) {
      const shipmentId = internationalShipmentIds[index];
      if (shipmentId == null) continue;
      const query = packageDetailsQueries[index];
      byId.set(shipmentId, {
        complete: query?.data != null && shipmentPackageDetailsAreComplete(query.data.packages),
      });
    }
    return byId;
  }, [internationalShipmentIds, packageDetailsQueries]);

  const outboundQueue = useMemo(() => {
    const rows: Array<{ key: string; sort: number; entry: OutboundQueueEntry }> = [];
    for (const shipment of shipments) {
      rows.push({
        key: `s-${shipment.shipment_id}`,
        sort: shipBySortKey({ kind: "shipment", shipment }),
        entry: { kind: "shipment", shipment },
      });
    }
    for (const transfer of transfers) {
      rows.push({
        key: `t-${transfer.id}`,
        sort: shipBySortKey({ kind: "transfer", transfer }),
        entry: { kind: "transfer", transfer },
      });
    }
    rows.sort((left, right) => left.sort - right.sort || left.key.localeCompare(right.key));
    return rows.map((row) => row.entry);
  }, [shipments, transfers]);

  const listLoading = shipmentsQuery.isLoading || transfersQuery.isLoading;
  const listFetching = shipmentsQuery.isFetching || transfersQuery.isFetching;

  function handleMarkPickedUp(entry: OutboundQueueEntry) {
    if (entry.kind === "shipment") {
      const { shipment } = entry;
      if (shipment.tracking && shipment.carrier) {
        markShippedWithTrackingMutation.mutate({
          kind: "shipment",
          entityId: shipment.shipment_id,
          carrier: shipment.carrier,
          tracking: shipment.tracking,
        });
      } else {
        setMarkShippedTarget({ kind: "shipment", row: shipment });
      }
      return;
    }
    const { transfer } = entry;
    if (transfer.tracking && transfer.carrier) {
      markShippedWithTrackingMutation.mutate({
        kind: "transfer",
        entityId: transfer.id,
        carrier: transfer.carrier,
        tracking: transfer.tracking,
      });
    } else {
      setMarkShippedTarget({ kind: "transfer", row: transfer });
    }
  }

  function openUpsModal(entry: OutboundQueueEntry, view: ShippingUpsView) {
    setUpsTarget(entry.kind === "shipment" ? { kind: "shipment", row: entry.shipment } : { kind: "transfer", row: entry.transfer });
    setUpsView(view);
    setUpsInitialLabels(undefined);
    const tracking = entry.kind === "shipment" ? entry.shipment.tracking : entry.transfer.tracking;
    if (view === "result" && tracking) {
      const trackingNo = tracking.split("/")[0];
      if (trackingNo) {
        void fetchUpsLabels(trackingNo).then((labels) => setUpsInitialLabels(labels));
      }
    }
  }

  function openPackingSlip(entry: OutboundQueueEntry) {
    setPackingSlipOpen({
      target:
        entry.kind === "shipment"
          ? { kind: "shipment", row: entry.shipment }
          : { kind: "transfer", row: entry.transfer },
      freightMode: false,
    });
  }

  function openPackageDetails(shipment: ShipmentRow) {
    setPackingSlipOpen({
      target: { kind: "shipment", row: shipment },
      freightMode: true,
    });
  }

  return (
    <div className="space-y-6">
      {prePackLaborFlash ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">{prePackLaborFlash}</span>
          <button
            type="button"
            className="shrink-0 text-amber-800 hover:text-amber-950"
            onClick={() => setPrePackLaborFlash(null)}
            aria-label="Dismiss Pre-Pack message"
          >
            <XCircle className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Outbound</h1>
          <p className="mt-1 text-sm text-slate-500">
            Customer shipments and outbound transfer orders from Smart Grid Solutions, sorted by ship-by date.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LaborStationOperatorPresence stationPage="shipping-team" />
          <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void shipmentsQuery.refetch();
            void transfersQuery.refetch();
          }}
          disabled={listFetching}
        >
          {listFetching ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          )}
          Refresh
        </Button>
        </div>
      </header>

      <SharedLaborTracker ref={laborRef} />

      {actionError ? (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{actionError}</span>
          <button
            type="button"
            className="ml-auto shrink-0 text-rose-600 hover:text-rose-800"
            onClick={() => setActionError(null)}
          >
            ✕
          </button>
        </div>
      ) : null}

      {loadError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load shipments: {loadError}
        </p>
      ) : null}

      {transfersLoadError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load transfers: {transfersLoadError}
        </p>
      ) : null}

      {listLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading outbound queue…
        </div>
      ) : outboundQueue.length === 0 ? (
        <Card>
          <CardBody>
            <p className="py-8 text-center text-sm text-slate-500">
              Nothing in the outbound queue. Shipments appear here once they are sent to pack-out and fully boxed.
              Transfer orders appear when they are ready to ship from SGS.
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            {outboundQueue.length} outbound line{outboundQueue.length !== 1 ? "s" : ""}
          </p>
          {outboundQueue.map((entry) => (
            <OutboundQueueCard
              key={entry.kind === "shipment" ? `s-${entry.shipment.shipment_id}` : `t-${entry.transfer.id}`}
              entry={entry}
              onToggleReady={() =>
                entry.kind === "shipment"
                  ? toggleReadyMutation.mutate(entry.shipment.shipment_id)
                  : toggleTransferReadyMutation.mutate(entry.transfer.id)
              }
              onMarkShipped={() => handleMarkPickedUp(entry)}
              onOpenPackingSlip={() => openPackingSlip(entry)}
              showFreightDocs={
                entry.kind === "shipment" &&
                isInternationalShipTo(entry.shipment.ship_to_country, entry.shipment.ship_to_state)
              }
              showUps={
                entry.kind !== "shipment" ||
                !isInternationalShipTo(entry.shipment.ship_to_country, entry.shipment.ship_to_state)
              }
              packageDetailsComplete={
                entry.kind === "shipment"
                  ? Boolean(packageDetailsByShipmentId.get(entry.shipment.shipment_id)?.complete)
                  : false
              }
              freightDocsPrintPending={
                freightDocsMutation.isPending &&
                freightDocsMutation.variables?.mode === "print" &&
                freightDocsMutation.variables.shipmentId ===
                  (entry.kind === "shipment" ? entry.shipment.shipment_id : undefined)
              }
              freightDocsDownloadPending={
                freightDocsMutation.isPending &&
                freightDocsMutation.variables?.mode === "download" &&
                freightDocsMutation.variables.shipmentId ===
                  (entry.kind === "shipment" ? entry.shipment.shipment_id : undefined)
              }
              onOpenPackageDetails={
                entry.kind === "shipment" ? () => openPackageDetails(entry.shipment) : undefined
              }
              onPrintFreightDocs={
                entry.kind === "shipment"
                  ? () => {
                      setActionError(null);
                      setFreightPrintShipment(entry.shipment);
                    }
                  : undefined
              }
              onDownloadFreightDocs={
                entry.kind === "shipment"
                  ? () => {
                      setActionError(null);
                      freightDocsMutation.mutate({
                        shipmentId: entry.shipment.shipment_id,
                        mode: "download",
                      });
                    }
                  : undefined
              }
              onOpenUps={(view) => openUpsModal(entry, view)}
              showReprintCartonLabel={outboundReprintTargetFromEntry(entry) != null}
              onReprintCartonLabel={() => {
                const reprintTarget = outboundReprintTargetFromEntry(entry);
                if (reprintTarget != null) setReprintCartonTarget(reprintTarget);
              }}
              isTogglingReady={
                entry.kind === "shipment"
                  ? toggleReadyMutation.isPending && toggleReadyMutation.variables === entry.shipment.shipment_id
                  : toggleTransferReadyMutation.isPending && toggleTransferReadyMutation.variables === entry.transfer.id
              }
              isMarkingShipped={
                entry.kind === "shipment"
                  ? (markShippedMutation.isPending
                      && markShippedTarget?.kind === "shipment"
                      && markShippedTarget.row.shipment_id === entry.shipment.shipment_id)
                    || (markShippedWithTrackingMutation.isPending
                      && markShippedWithTrackingMutation.variables?.kind === "shipment"
                      && markShippedWithTrackingMutation.variables.entityId === entry.shipment.shipment_id)
                  : (markTransferSentMutation.isPending
                      && markShippedTarget?.kind === "transfer"
                      && markShippedTarget.row.id === entry.transfer.id)
                    || (markShippedWithTrackingMutation.isPending
                      && markShippedWithTrackingMutation.variables?.kind === "transfer"
                      && markShippedWithTrackingMutation.variables.entityId === entry.transfer.id)
              }
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {markShippedTarget ? (
        <MarkShippedModal
          target={markShippedTarget}
          carriers={carriers}
          carriersLoading={carriersQuery.isLoading}
          onClose={() => setMarkShippedTarget(null)}
          onConfirm={(carrier, tracking) => {
            if (markShippedTarget.kind === "shipment") {
              markShippedMutation.mutate({
                shipmentId: markShippedTarget.row.shipment_id,
                carrier,
                tracking,
              });
            } else {
              markTransferSentMutation.mutate({
                transferId: markShippedTarget.row.id,
                carrier,
                tracking,
              });
            }
          }}
          isPending={
            markShippedTarget.kind === "shipment"
              ? markShippedMutation.isPending
              : markTransferSentMutation.isPending
          }
        />
      ) : null}

      {packingSlipOpen ? (
        <PackingSlipModal
          target={packingSlipOpen.target}
          freightMode={packingSlipOpen.freightMode}
          onClose={() => setPackingSlipOpen(null)}
          onDone={() => setPackingSlipOpen(null)}
        />
      ) : null}

      {freightPrintShipment ? (
        <FreightDocsPrintModal
          shipment={freightPrintShipment}
          pending={
            freightDocsMutation.isPending &&
            freightDocsMutation.variables?.mode === "print" &&
            freightDocsMutation.variables.shipmentId === freightPrintShipment.shipment_id
          }
          error={
            freightDocsMutation.isError &&
            freightDocsMutation.variables?.mode === "print" &&
            freightDocsMutation.variables.shipmentId === freightPrintShipment.shipment_id
              ? freightDocsMutation.error instanceof ApiError
                ? freightDocsMutation.error.message
                : String(freightDocsMutation.error)
              : null
          }
          onClose={() => {
            if (!freightDocsMutation.isPending) setFreightPrintShipment(null);
          }}
          onPrint={(printer) => {
            freightDocsMutation.mutate({
              shipmentId: freightPrintShipment.shipment_id,
              mode: "print",
              printer,
            });
          }}
        />
      ) : null}

      {upsTarget ? (
        <UpsModal
          target={upsTarget}
          initialView={upsView}
          initialLabels={upsInitialLabels}
          onClose={() => setUpsTarget(null)}
          onTrackingChanged={() => {
            void queryClient.invalidateQueries({
              queryKey: upsTarget.kind === "shipment" ? SHIPMENTS_QUERY_KEY : TRANSFERS_QUERY_KEY,
            });
          }}
          onShipmentActioned={(entityId) => {
            laborRef.current?.recordShipment(entityId);
          }}
        />
      ) : null}

      {reprintCartonTarget ? (
        <OutboundReprintCartonLabelModal
          target={reprintCartonTarget}
          onClose={() => setReprintCartonTarget(null)}
        />
      ) : null}
    </div>
  );
}

